import { format } from 'date-fns';
import * as locales from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { formatNumericValue } from 'utils/general';

import { useContractDetails } from '@/modules/workforce/Contracts/components/Details/logic';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { Typography } from '@/shared/components/atoms';
import IconWithTitleDescription from '@/shared/components/atoms/IconWithTItleDescription';
import { IIconWithTitleDescriptionProps } from '@/shared/components/atoms/IconWithTItleDescription/IconWithTitleDescription';
import { useStore } from '@/shared/hooks/context/createGenericContext';

import Building from '~/images/shared/building-3.svg';
import Calender from '~/images/shared/calender.svg';
import Clock from '~/images/shared/Clock.svg';
import Coin from '~/images/shared/Coin.svg';
import DollarCircle from '~/images/shared/dollarCircle.svg';
import Profile from '~/images/shared/Profile.svg';
import Receipt from '~/images/shared/Receipt.svg';

const ContractGeneralInformation: React.FC<{
  contract: IContracts.IContractDetails;
}> = ({ contract }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [selectedAsset] = useStore((store) => store?.asset);

  const { getWorkingHours, getOvertime } = useContractDetails();

  const sharedProps = {
    iconGap: 'gap-16',
    iconAlignment: 'center' as const,
    descriptionTypography: {
      weight: 'medium' as const,
      variant: 'subtitle' as const,
    },
  };

  const commonInfo: IIconWithTitleDescriptionProps[] = [
    {
      icon: <Building width='30' height='30' />,
      title: t('Job Location'),
      description: selectedAsset?.name,
    },
    {
      icon: <Profile width='30' height='30' />,
      title: t('Crew'),
      description: contract.supplierName,
    },
    {
      icon: <Calender width='30' height='30' />,
      title: t('Contract Duration'),
      description: `${format(
        new Date(contract.startDate.replace(/Z$/, '')),
        'd MMM yyyy',
        {
          locale: locales[language as keyof typeof locales],
        }
      )} - ${format(
        new Date(contract.endDate.replace(/Z$/, '')),
        'd MMM yyyy',
        {
          locale: locales[language as keyof typeof locales],
        }
      )}`,
    },
    // {
    //   icon: <People width='30' height='30' />,
    //   title: t('Workers'),
    //   description: formatNumericValue(contract.numberOfWorkers),
    // },
  ];

  const dailyRateInfo: IIconWithTitleDescriptionProps[] = [
    {
      icon: <DollarCircle width='30' height='30' />,
      iconAlignment: 'center',
      title: t('Daily Rate'),
      description: `${formatNumericValue(contract.dailyRate ?? 0)}${
        selectedAsset?.currency ? ` ${t(selectedAsset.currency)}` : ''
      }`,
    },
    {
      icon: <Clock width='30' height='30' />,
      iconAlignment: 'center',
      title: t('Working Hours'),
      description: getWorkingHours(contract.intervals || []),
    },
    {
      icon: <Coin width='30' height='30' />,
      iconAlignment: 'center',
      title: t('Overtime'),
      description: `${getOvertime(contract.intervals || [])}${
        selectedAsset?.currency
          ? ` ${t(selectedAsset.currency)}/${t('hour')}`
          : ''
      }`,
    },
  ];

  return (
    <div className='flex flex-col gap-24'>
      <div>
        <Typography className='mb-8' weight='bold' variant='h3'>
          {t('general-information#002')}
        </Typography>
        <div className='flex'>
          {commonInfo.map((item) => (
            <div key={item.title} className='w-full'>
              <IconWithTitleDescription item={{ ...item, ...sharedProps }} />
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className='flex items-center gap-8'>
          <Typography variant='h3'>{`${t('Contract Type')}:`}</Typography>
          <Receipt width='30' height='30' />
          <Typography variant='h3' weight='bold'>
            {t(contract.dailyWage ? 'daily' : 'piecework-rate')}
          </Typography>
        </div>
        {!!contract.dailyWage && (
          <div className='mt-8 flex'>
            {dailyRateInfo.map((item) => (
              <div key={item.title} className='w-full'>
                <IconWithTitleDescription item={{ ...item, ...sharedProps }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractGeneralInformation;
